import * as React from 'react'
import { EditorState } from 'prosemirror-state'
import { type EditorView } from 'prosemirror-view'

import { CellSelection } from 'common/Prose/utils/table/CellSelection'
import { ReactComponent as AlignmentIcon } from 'assets/icons/alignVerticalTop24.svg'
import { ReactComponent as BorderColorIcon } from 'assets/icons/borderColor24.svg'
import { ReactComponent as BorderStyleIcon } from 'assets/icons/borderStyle24.svg'
import { ReactComponent as BorderThicknessIcon } from 'assets/icons/borderThickness24.svg'
import { ReactComponent as CellColorIcon } from 'assets/icons/cellcolor24.svg'
import { ReactComponent as CopyIcon } from 'assets/icons/copy16-flipped.svg'
import { ReactComponent as CutIcon } from 'assets/icons/cut16.svg'
import { ReactComponent as DeleteColumn } from 'assets/icons/deleteColumn16.svg'
import { ReactComponent as DeleteRow } from 'assets/icons/deleteRow16.svg'
import { ReactComponent as FitTableToDocIcon } from 'assets/icons/fitTableToDoc16.svg'
import { ReactComponent as MergeSplitIcon } from 'assets/icons/mergeCell16.svg'
import { ReactComponent as PasteIcon } from 'assets/icons/paste16.svg'
import { ReactSVGElement } from 'types'
import { TableBorderColorPicker } from 'common/Prose/components/TableBorderColorPicker/TableBorderColorPicker'
import { TableBorderStylePicker } from 'common/Prose/components/TableBorderStylePicker/TableBorderStylePicker'
import { TableBorderThicknessPicker } from 'common/Prose/components/TableBorderThicknessPicker/TableBorderThicknessPicker'
import { TableCellColorPicker } from 'common/Prose/components/TableCellColorPicker/TableCellColorPicker'
import { VerticalAlignmentPicker } from 'common/Prose/components/VerticalAlignmentPicker/VerticalAlignmentPicker'
import {
  addColumnAfter,
  addColumnBefore,
  addRowAfter,
  addRowBefore,
  deleteColumnsInSelection,
  deleteRowsInSelection,
  distributeColumns,
  mergeCells,
  splitCell,
} from 'common/Prose/Commands/table/mutation'
import { canMergeCells } from '../tableSelection/tableSelectionUtils'
import { copySelection } from './copySelection'
import { isTableSelection } from 'common/Prose/utils/table'
import { tryGetTrackChangesState } from '../TrackChanges/constants'
import { ColumnLeft, ColumnRight, RowAbove, RowBelow } from 'almanac-ui/icons'

export interface TableMenuItem {
  label: string
  testId: string
  icon: React.ComponentType<React.PropsWithChildren<any>>
  run: (view: EditorView, position: { x: number; y: number }, event: React.MouseEvent) => void
  enable: (state: EditorState) => boolean
}

export interface FormatOption {
  label: string
  icon: ReactSVGElement
  content: (view: EditorView, closeMenu: Function) => React.ReactNode
  enable: (state: EditorState) => boolean
}

export const ClipboardOptions: TableMenuItem[] = [
  {
    label: 'Cut',
    testId: 'cut',
    icon: CutIcon,
    run: copySelection(true),
    enable: () => true,
  },
  {
    label: 'Copy',
    testId: 'copy',
    icon: CopyIcon,
    run: copySelection(),
    enable: () => true,
  },
  {
    label: 'Paste',
    testId: 'paste',
    icon: PasteIcon,
    run: async (view) => {
      const { state } = await navigator.permissions.query({ name: 'clipboard-read' as PermissionName })
      if (state !== 'granted' && state !== 'prompt') return

      const clipboardItems = await navigator.clipboard.read()
      let htmlString = null

      for (const clipboardItem of clipboardItems) {
        if (clipboardItem.types.includes('text/html'))
          htmlString = await (await clipboardItem.getType('text/html')).text()
      }

      if (!htmlString) return

      const clipboardData = new DataTransfer()
      clipboardData.setData('text/html', htmlString)
      const pasteEvent = new ClipboardEvent('paste', { clipboardData })

      view.eventHandlers.paste(pasteEvent)
      view.focus()
    },
    enable: (state) =>
      isTableSelection(state.selection) ||
      (state.selection instanceof CellSelection && state.selection.isSingleCellSelection),
  },
]

export const FormatOptions: FormatOption[] = [
  {
    label: 'Alignment',
    icon: AlignmentIcon,
    enable: () => true,
    content: (view, closeMenu) => <VerticalAlignmentPicker view={view} closePicker={() => closeMenu(view)} />,
  },
  {
    label: 'Cell color',
    icon: CellColorIcon,
    enable: () => true,
    content: (view, closeMenu) => <TableCellColorPicker view={view} closePicker={() => closeMenu(view)} />,
  },
  {
    label: 'Border color',
    icon: BorderColorIcon,
    enable: () => true,
    content: (view, closeMenu) => <TableBorderColorPicker view={view} closePicker={() => closeMenu(view)} />,
  },
  {
    label: 'Thickness',
    icon: BorderThicknessIcon,
    enable: () => true,
    content: (view, closeMenu) => <TableBorderThicknessPicker view={view} closePicker={() => closeMenu(view)} />,
  },
  {
    label: 'Style',
    enable: () => true,
    content: (view, closeMenu) => <TableBorderStylePicker view={view} closePicker={() => closeMenu(view)} />,
    icon: BorderStyleIcon,
  },
]

export const InsertOptions: TableMenuItem[] = [
  {
    label: 'Insert row above',
    testId: 'insert-row-above',
    icon: RowAbove,
    run: (view) => {
      addRowBefore(view.state, view.dispatch)
      view.focus()
    },
    enable: () => true,
  },
  {
    label: 'Insert row below',
    testId: 'insert-row-below',
    icon: RowBelow,
    run: (view) => {
      addRowAfter(view.state, view.dispatch)
      view.focus()
    },
    enable: () => true,
  },
  {
    label: 'Insert column left',
    testId: 'insert-column-left',
    icon: ColumnLeft,
    run: (view) => {
      addColumnBefore(view.state, view.dispatch)
      view.focus()
    },
    enable: () => true,
  },
  {
    label: 'Insert column right',
    testId: 'insert-column-right',
    icon: ColumnRight,
    run: (view) => {
      addColumnAfter(view.state, view.dispatch)
      view.focus()
    },
    enable: () => true,
  },
]

export const MergeOptions: TableMenuItem[] = [
  {
    label: 'Merge cells',
    testId: 'merge-selected-cells',
    icon: MergeSplitIcon,
    run: (view) => {
      mergeCells(view.state, view.dispatch)
      view.focus()
    },
    enable: (state) => {
      const trackChangesState = tryGetTrackChangesState(state)
      if (trackChangesState?.store.trackingChanges) {
        return false
      }
      const { selection } = state
      return selection instanceof CellSelection && canMergeCells(selection)
    },
  },
  {
    label: 'Split cell',
    testId: 'split-selected-cell',
    icon: MergeSplitIcon,
    run: (view) => {
      splitCell(view.state, view.dispatch)
      view.focus()
    },
    enable: (state) => {
      const { selection } = state
      let cellNode
      if (!(selection instanceof CellSelection)) {
        return false
      } else {
        if (selection.$anchorCell.pos !== selection.$headCell.pos) return false
        cellNode = selection.$anchorCell.nodeAfter
        if (!cellNode || (cellNode.attrs.colspan === 1 && cellNode.attrs.rowspan === 1)) {
          return false
        }
      }
      return true
    },
  },
  {
    label: 'Fit to document',
    testId: 'fit-table-to-document',
    icon: FitTableToDocIcon,
    run: (view) => {
      const tablePos = view.state.selection.from
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const tableEl = view.nodeDOM(tablePos)! as HTMLElement
      distributeColumns(tableEl.offsetWidth, tablePos)(view.state, view.dispatch)
    },
    enable: (state) => isTableSelection(state.selection),
  },
]

export const RemoveOptions: TableMenuItem[] = [
  {
    label: 'Remove row',
    testId: 'delete-row',
    icon: DeleteRow,
    run: (view) => {
      deleteRowsInSelection(view.state, view.dispatch)
      view.focus()
    },
    enable: () => true,
  },
  {
    label: 'Remove column',
    testId: 'delete-column',
    icon: DeleteColumn,
    run: (view) => {
      deleteColumnsInSelection(view.state, view.dispatch)
      view.focus()
    },
    enable: (state) => {
      const trackChangesState = tryGetTrackChangesState(state)
      if (trackChangesState?.store.trackingChanges) {
        return false
      }

      return true
    },
  },
]
